import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

const clients = [
    { name: "BookMyShow", logo: "/assets/logos/logo1.svg" },
    { name: "Ecolab", logo: "/assets/logos/logo2.svg" },
    { name: "J.P. Morgan", logo: "/assets/logos/logo3.svg" },
    { name: "Brandix", logo: "/assets/logos/logo4.svg" },
    { name: "ABB", logo: "/assets/logos/logo5.svg" },
    { name: "IKEA", logo: "/assets/logos/logo8.svg" },
    { name: "QWIC", logo: "/assets/logos/logo6.svg" },
    { name: "STL", logo: "/assets/logos/logo10.svg" },
    { name: "Tenerity", logo: "/assets/logos/logo9.svg" },
    { name: "Bahrain Duty Free", logo: "/assets/logos/logo7.svg" },
    { name: "K-Flex", logo: "/assets/logos/logo11.svg" },
    { name: "Honeywell", logo: "/assets/logos/logo12.svg" },
    { name: "Cargo Service Center", logo: "/assets/logos/logo13.svg" },
    { name: "HPX", logo: "/assets/logos/logo14.svg" },
    { name: "YK Electric", logo: "/assets/logos/logo15.svg" },
    { name: "Fazar", logo: "/assets/logos/logo16.svg" },
    { name: "Logo 17", logo: "/assets/logos/logo17.svg" },
    { name: "Logo 18", logo: "/assets/logos/logo18.svg" },
];

const ClientsCareersSection = () => {
    return (
        <section className="pb-[60px] lg:pb-[120px] pt-[40px] lg:pt-[80px] bg-white font-sans relative overflow-hidden">
            <div className="absolute bottom-0 left-0 w-full h-[60%] z-0 opacity-30 pointer-events-none">
                <FlowingMeshCanvasAlt />
            </div>

            <div className="max-w-[1220px] w-full mx-auto px-[20px] lg:px-[80px] relative z-10">
                <div className="mb-[60px] lg:mb-[100px]">
                    <h2 className="text-[#01386A] font-sora text-[20px] lg:text-[30px] font-[600] relative z-10 mb-[40px] lg:mb-[60px] leading-tight text-center lg:text-left">
                        Delivering for Global Leaders at Scale
                    </h2>

                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-x-[10px] lg:gap-x-[16px] gap-y-[16px] lg:gap-y-[16px] items-center justify-items-center">
                        {clients.map((client, index) => {
                            // Conditions to increase scale for specific logos
                            {/* const isLargeLogo =
                                client.name === "Bahrain Duty Free" ||
                                client.name === "Logo 17"; */}

                            return (
                                <div
                                    key={index}
                                    className={`w-full flex items-center justify-center transition-all duration-300 cursor-pointer group`}
                                >
                                    <img
                                        src={client.logo}
                                        alt={client.name}
                                        className={`max-w-full max-h-full object-contain transition-all duration-300}`}
                                    />
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-[20px] lg:gap-[30px]">
                    <TalentCard
                        title="We are always searching for the best talent"
                        linkText="View All Global Openings"
                        link="/careers"
                        bgImage="/assets/home-/img4.png"
                    />
                    <TalentCard
                        title="Let’s Discuss Your Business and Technology Needs"
                        linkText="Explore Services"
                        link="/what-we-do"
                        bgImage="/assets/home-/img5.jpg"
                    />
                </div>
            </div>
        </section>
    );
};

// ... TalentCard and FlowingMeshCanvasAlt remain the same as your provided code ...

const TalentCard = ({ title, linkText, bgImage, link }) => {
    return (
        <motion.div
            initial="rest"
            whileHover="hover"
            animate="rest"
            variants={{
                rest: { y: 0, boxShadow: "0 10px 30px -15px rgba(0, 0, 0, 0.3)" },
                hover: { y: -8, boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)" }
            }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            className="group relative h-[320px] sm:h-[350px] lg:h-[400px] overflow-hidden cursor-pointer"
        >
            <div className="absolute inset-0 overflow-hidden">
                <motion.img
                    src={bgImage}
                    alt="Career Background"
                    variants={{
                        rest: { scale: 1 },
                        hover: { scale: 1.1 }
                    }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                    className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-[#01386A4D] z-0" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#01386A]/90 via-[#01386A]/20 to-transparent z-0" />
            </div>

            <div className="absolute bottom-0 left-0 w-full p-[24px] lg:p-[40px] z-10 flex flex-col justify-end h-full">
                <motion.h3
                    variants={{
                        rest: { y: 0 },
                        hover: { y: -5 }
                    }}
                    transition={{ duration: 0.4, ease: "easeOut" }}
                    className="text-white text-[20px] lg:text-[30px] font-[600] font-sora leading-[1.2] mb-4"
                >
                    {title}
                </motion.h3>

                <Link to={link || "#"} className="inline-block">
                    <div className="flex items-center gap-2 text-white font-[600] text-[14px] lg:text-[16px]">
                        {linkText}
                        <ChevronRight size={18} className="group-hover:translate-x-2 transition-transform duration-300" />
                    </div>
                    <div className="h-[2px] bg-[#F28C28] mt-2 w-0 group-hover:w-full transition-all duration-300" />
                </Link>
            </div>
        </motion.div>
    );
};

const FlowingMeshCanvasAlt = () => {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        let animationFrameId;
        let time = 0;

        const resizeCanvas = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };
        window.addEventListener("resize", resizeCanvas);
        resizeCanvas();

        const draw = () => {
            const { width, height } = canvas;
            ctx.clearRect(0, 0, width, height);
            const lines = 15;
            const amplitude = 70;
            const frequency = 0.002;
            const speed = 0.002;
            ctx.lineWidth = 1;
            for (let i = 0; i < lines; i++) {
                ctx.beginPath();
                const alpha = 0.2 + (i / lines) * 0.3;
                ctx.strokeStyle = `hsla(217, 60%, 40%, ${alpha})`;
                const yOffset = height / 1.4;
                const timeOffset = i * 0.1 - time;
                for (let x = 0; x <= width + 40; x += 40) {
                    const y1 = Math.sin(x * frequency + timeOffset) * amplitude;
                    const tilt = (x / width) * -120;
                    ctx.lineTo(x, yOffset + y1 + tilt + (i * 5));
                }
                ctx.stroke();
            }
            time += speed;
            animationFrameId = requestAnimationFrame(draw);
        };
        draw();
        return () => {
            window.removeEventListener("resize", resizeCanvas);
            cancelAnimationFrame(animationFrameId);
        };
    }, []);

    return <canvas ref={canvasRef} className="w-full h-full" />;
};

export default ClientsCareersSection;